import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { AnnouncementsService } from 'app/modules/admin/apps/announcements/announcements.service';
import { Announcement } from 'app/modules/admin/apps/announcements/announcements.types';
import { MarkdownPipe } from 'app/shared/pipes/markdown.pipe';

/**
 * Full-screen blocking overlay for a frontend version gate.
 *
 * When an admin publishes a `blocking` announcement carrying a minFrontendVersion,
 * every tab running an OLDER baked version gets this overlay — decided per running
 * client by AnnouncementsService.versionGate$ (not per user). It cannot be
 * dismissed; the only action is to log out and back in, which full-loads the new
 * bundle. Mounted once in the layout shell so it covers every layout variant
 * (incl. kiosk / empty).
 */
@Component({
    selector       : 'version-gate',
    templateUrl    : './version-gate.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone     : true,
    imports        : [MatButtonModule, MatIconModule, MarkdownPipe]
})
export class VersionGateComponent implements OnInit, OnDestroy {
    gate: Announcement | null = null;
    private _unsubscribeAll = new Subject<void>();

    constructor(
        private _service: AnnouncementsService,
        private _authService: AuthService,
        private _cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this._service.versionGate$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((gate) => {
                this.gate = gate;
                this._cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    /**
     * Log out and full-load the sign-in page so the browser fetches the new
     * bundle. The session is cleared first so the sign-in route shows the login
     * form instead of bouncing an authenticated user back into the stale app.
     */
    reload(): void {
        try {
            this._authService.signOut2();
        } catch {
            /* ignore — fall through to the hard reload regardless */
        }
        window.location.assign('/sign-in');
    }
}
